import { useQuery } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components as AssetComponents } from '@/api/types/asset-service';

// Per-screen query layer for S3 Asset Detail. Each hook is single-purpose
// so the screen file stays declarative — load → loading → error → render.
//
// All four endpoints are read-only per Phase 3 (1/n) + (3/n):
//   GET /v1/assets/{id}              — full record + topology
//   GET /v1/assets/{id}/diagnosis    — current snapshot
//   GET /v1/assets/{id}/sensors/health — per-sensor rollup
//   GET /v1/assets/{id}/health-history — TrendTrack input

export type AssetDetail = AssetComponents['schemas']['AssetDetail'];
export type AssetDiagnosis = AssetComponents['schemas']['AssetDiagnosis'];
export type AssetSensorsHealth = AssetComponents['schemas']['AssetSensorsHealth'];
export type HealthHistory = AssetComponents['schemas']['HealthHistoryResponse'];

function unwrap<T>(
  data: T | undefined,
  error: unknown,
  what: string,
): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export function useAssetDetail(clients: ApiClients, assetId: string | undefined) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['asset', assetId, 'detail'],
    queryFn: async (): Promise<AssetDetail> => {
      const { data, error } = await clients.asset.GET('/v1/assets/{assetId}', {
        params: { path: { assetId: assetId! } },
      });
      return unwrap(data, error, 'asset detail');
    },
    staleTime: 30_000,
  });
}

export function useAssetDiagnosis(clients: ApiClients, assetId: string | undefined) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['asset', assetId, 'diagnosis'],
    queryFn: async (): Promise<AssetDiagnosis> => {
      const { data, error } = await clients.asset.GET('/v1/assets/{assetId}/diagnosis', {
        params: { path: { assetId: assetId! } },
      });
      return unwrap(data, error, 'asset diagnosis');
    },
    staleTime: 30_000,
  });
}

export function useAssetSensorsHealth(clients: ApiClients, assetId: string | undefined) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['asset', assetId, 'sensors-health'],
    queryFn: async (): Promise<AssetSensorsHealth> => {
      const { data, error } = await clients.asset.GET(
        '/v1/assets/{assetId}/sensors/health',
        { params: { path: { assetId: assetId! } } },
      );
      return unwrap(data, error, 'sensors health');
    },
    staleTime: 30_000,
  });
}

export function useAssetHealthHistory(
  clients: ApiClients,
  assetId: string | undefined,
  days: 30 | 90 | 365 = 30,
) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['asset', assetId, 'health-history', days],
    queryFn: async (): Promise<HealthHistory> => {
      const { data, error } = await clients.asset.GET(
        '/v1/assets/{assetId}/health-history',
        {
          params: {
            path: { assetId: assetId! },
            query: { days, bucket: 'day' },
          },
        },
      );
      return unwrap(data, error, 'health history');
    },
    staleTime: 60_000,
  });
}
